import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ClipboardList, ArrowRight, Calendar, Search, ChevronLeft, ChevronRight, MoreHorizontal, BarChart3, Plus, Edit, Trash2, X, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    submitTatAnalysis,
    getMyTatAnalysis,
    getTatStatistics,
    updateTatAnalysis,
    deleteTatAnalysis
} from '../actions/tatAnalysisAction';
import { getMyVerificationProcesses } from '../actions/verificationProcessActions';
import {
    SUBMIT_TAT_ANALYSIS_RESET,
    GET_MY_TAT_ANALYSIS_RESET,
    GET_TAT_STATISTICS_RESET,
    UPDATE_TAT_ANALYSIS_RESET,
    DELETE_TAT_ANALYSIS_RESET
} from '../constants/tatAnalysisConstant';

// Enhanced Pagination Component
const EnhancedPagination = ({
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    onPageChange,
    onItemsPerPageChange,
    className = "",
    showItemsPerPage = true,
    showInfo = true,
    maxVisiblePages = 5
}) => {
    const [itemsPerPageOptions] = useState([5, 10, 15, 20, 25, 50]);

    const getVisiblePages = () => {
        const pages = [];
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    };

    const visiblePages = getVisiblePages();
    const showStartEllipsis = visiblePages[0] > 2;
    const showEndEllipsis = visiblePages[visiblePages.length - 1] < totalPages - 1;

    const startItem = ((currentPage - 1) * itemsPerPage) + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    const handlePrevious = () => {
        if (currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };

    const handleNext = () => {
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
        }
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        onItemsPerPageChange(newItemsPerPage);
        const newTotalPages = Math.ceil(totalItems / newItemsPerPage);
        if (currentPage > newTotalPages) {
            onPageChange(newTotalPages);
        }
    };

    if (totalPages <= 1 && !showInfo) return null;

    return (
        <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 py-4 px-6 bg-gray-50 border-t ${className}`}>
            {showItemsPerPage && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>Rows per page</span>
                    <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                    >
                        {itemsPerPageOptions.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {showInfo && (
                <div className="text-sm text-gray-600 order-first sm:order-none">
                    {totalItems > 0 ? (
                        <>
                            Showing {startItem} to {endItem} of {totalItems} entries
                        </>
                    ) : (
                        'No entries found'
                    )}
                </div>
            )}

            {totalPages > 1 && (
                <div className="flex items-center gap-1">
                    <button
                        onClick={handlePrevious}
                        disabled={currentPage === 1}
                        className={`flex items-center gap-1 px-3 py-1 text-sm rounded ${currentPage === 1
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-100'
                            }`}
                    >
                        <ChevronLeft size={16} />
                        Previous
                    </button>

                    {visiblePages[0] > 1 && (
                        <>
                            <button
                                onClick={() => onPageChange(1)}
                                className="w-8 h-8 flex items-center justify-center text-sm rounded hover:bg-gray-100"
                            >
                                1
                            </button>
                            {showStartEllipsis && (
                                <div className="flex items-center justify-center w-8 h-8">
                                    <MoreHorizontal size={16} className="text-gray-400" />
                                </div>
                            )}
                        </>
                    )}

                    {visiblePages.map(page => (
                        <button
                            key={page}
                            onClick={() => onPageChange(page)}
                            className={`w-8 h-8 flex items-center justify-center text-sm rounded ${page === currentPage
                                ? 'bg-gray-900 text-white'
                                : 'text-gray-700 hover:bg-gray-100'
                                }`}
                        >
                            {page}
                        </button>
                    ))}

                    {visiblePages[visiblePages.length - 1] < totalPages && (
                        <>
                            {showEndEllipsis && (
                                <div className="flex items-center justify-center w-8 h-8">
                                    <MoreHorizontal size={16} className="text-gray-400" />
                                </div>
                            )}
                            <button
                                onClick={() => onPageChange(totalPages)}
                                className="w-8 h-8 flex items-center justify-center text-sm rounded hover:bg-gray-100"
                            >
                                {totalPages}
                            </button>
                        </>
                    )}

                    <button
                        onClick={handleNext}
                        disabled={currentPage === totalPages}
                        className={`flex items-center gap-1 px-3 py-1 text-sm rounded ${currentPage === totalPages
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-100'
                            }`}
                    >
                        Next
                        <ChevronRight size={16} />
                    </button>
                </div>
            )}
        </div>
    );
};

const TatAnalysis = () => {
    const dispatch = useDispatch();

    // Redux state selectors
    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;

    const submitTatAnalysisInfo = useSelector((state) => state.submitTatAnalysisInfo);
    const { loadingSubmitTatAnalysis, errorSubmitTatAnalysis, submitTatAnalysisData } = submitTatAnalysisInfo;

    const myTatAnalysisInfo = useSelector((state) => state.myTatAnalysisInfo);
    const { loadingMyTatAnalysis, errorMyTatAnalysis, myTatAnalysisData } = myTatAnalysisInfo;

    const getVerificationProcessesInfo = useSelector((state) => state.getVerificationProcessesInfo);
    const { loadingGetVerificationProcesses, errorGetVerificationProcesses, getVerificationProcessesData } = getVerificationProcessesInfo;

    const tatStatisticsInfo = useSelector((state) => state.tatStatisticsInfo);
    const { loadingTatStatistics, errorTatStatistics, tatStatisticsData } = tatStatisticsInfo;

    const updateTatAnalysisInfo = useSelector((state) => state.updateTatAnalysisInfo);
    const { loadingUpdateTatAnalysis, errorUpdateTatAnalysis, updateTatAnalysisData } = updateTatAnalysisInfo;

    const deleteTatAnalysisInfo = useSelector((state) => state.deleteTatAnalysisInfo);
    const { loadingDeleteTatAnalysis, errorDeleteTatAnalysis, deleteTatAnalysisData } = deleteTatAnalysisInfo;

    // Local state
    const [isOpen, setIsOpen] = useState(false);
    const [formData, setFormData] = useState({
        process: '',
        tatCategories: {
            '0-2': { numberOfCases: '', tatValue: '' },
            '2-4': { numberOfCases: '', tatValue: '' },
            '4-6': { numberOfCases: '', tatValue: '' },
            '6-8': { numberOfCases: '', tatValue: '' },
            '>8': { numberOfCases: '', tatValue: '' }
        }
    });
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [editGroup, setEditGroup] = useState(null);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState({
        startDate: '',
        endDate: ''
    });

    const tatCategories = ['0-2', '2-4', '4-6', '6-8', '>8'];

    // Get verification processes on component mount
    useEffect(() => {
        if (userInfo) {
            dispatch(getMyVerificationProcesses());
            dispatch(getTatStatistics());
        }
    }, [dispatch, userInfo]);

    // Set initial process when verification processes are loaded
    useEffect(() => {
        if (getVerificationProcessesData && getVerificationProcessesData.data && getVerificationProcessesData.data.length > 0) {
            const firstProcess = getVerificationProcessesData.data[0]._id;
            setFormData(prev => ({
                ...prev,
                process: firstProcess
            }));
        }
    }, [getVerificationProcessesData]);

    // Fetch TAT analysis when filters change
    useEffect(() => {
        if (userInfo) {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString()
            });

            if (searchTerm) params.append('search', searchTerm);
            if (dateFilter.startDate) params.append('startDate', dateFilter.startDate);
            if (dateFilter.endDate) params.append('endDate', dateFilter.endDate);

            dispatch(getMyTatAnalysis(params.toString()));
        }
    }, [dispatch, userInfo, page, limit, searchTerm, dateFilter.startDate, dateFilter.endDate]);

    // Handle submit success
    useEffect(() => {
        if (submitTatAnalysisData) {
            // toast.success('TAT Analysis submitted successfully!');
            const selectedProcess = formData.process;
            setFormData({
                process: selectedProcess,
                tatCategories: {
                    '0-2': { numberOfCases: '', tatValue: '' },
                    '2-4': { numberOfCases: '', tatValue: '' },
                    '4-6': { numberOfCases: '', tatValue: '' },
                    '6-8': { numberOfCases: '', tatValue: '' },
                    '>8': { numberOfCases: '', tatValue: '' }
                }
            });

            setIsEditing(false);
            setEditId(null);
            setIsOpen(false);

            // Refresh data
            dispatch(getTatStatistics());
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString()
            });
            dispatch(getMyTatAnalysis(params.toString()));

            setTimeout(() => {
                dispatch({ type: SUBMIT_TAT_ANALYSIS_RESET });
            }, 3000);
        }
    }, [submitTatAnalysisData, dispatch, formData.process, page, limit]);

    // Handle update success
    useEffect(() => {
        if (updateTatAnalysisData) {
            toast.success('TAT Analysis updated successfully!');
            setIsEditing(false);
            setEditId(null);
            setIsOpen(false);

            // Refresh data
            dispatch(getTatStatistics());
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString()
            });
            dispatch(getMyTatAnalysis(params.toString()));

            setTimeout(() => {
                dispatch({ type: UPDATE_TAT_ANALYSIS_RESET });
            }, 3000);
        }
    }, [updateTatAnalysisData, dispatch, page, limit]);

    // Handle delete success
    useEffect(() => {
        if (deleteTatAnalysisData) {
            toast.success('TAT Analysis deleted successfully!');
            // Refresh data
            dispatch(getTatStatistics());
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString()
            });
            dispatch(getMyTatAnalysis(params.toString()));

            setTimeout(() => {
                dispatch({ type: DELETE_TAT_ANALYSIS_RESET });
            }, 3000);
        }
    }, [deleteTatAnalysisData, dispatch, page, limit]);

    // Handle errors
    useEffect(() => {
        if (errorSubmitTatAnalysis) {
            toast.error(errorSubmitTatAnalysis);
        }
    }, [errorSubmitTatAnalysis]);

    useEffect(() => {
        if (errorUpdateTatAnalysis) {
            toast.error(errorUpdateTatAnalysis);
        }
    }, [errorUpdateTatAnalysis]);

    useEffect(() => {
        if (errorDeleteTatAnalysis) {
            toast.error(errorDeleteTatAnalysis);
        }
    }, [errorDeleteTatAnalysis]);

    useEffect(() => {
        if (errorMyTatAnalysis) {
            toast.error(errorMyTatAnalysis);
        }
    }, [errorMyTatAnalysis]);

    useEffect(() => {
        if (errorGetVerificationProcesses) {
            toast.error(errorGetVerificationProcesses);
        }
    }, [errorGetVerificationProcesses]);

    // Fixed handleChange function
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'process') {
            setFormData({ ...formData, [name]: value });
        } else {
            // Handle TAT category fields - format is "category-field" like "0-2-numberOfCases"
            // Special handling for ">8" category which contains special character
            let category, field;

            if (name.startsWith('>8-')) {
                category = '>8';
                field = name.replace('>8-', '');
            } else {
                const lastHyphenIndex = name.lastIndexOf('-');
                if (lastHyphenIndex !== -1) {
                    category = name.substring(0, lastHyphenIndex);
                    field = name.substring(lastHyphenIndex + 1);
                } else {
                    return; // Invalid field name format
                }
            }

            // Validate that the category exists in our form data
            if (formData.tatCategories.hasOwnProperty(category)) {
                setFormData(prev => ({
                    ...prev,
                    tatCategories: {
                        ...prev.tatCategories,
                        [category]: {
                            ...prev.tatCategories[category],
                            [field]: value === '' ? '' : (field === 'numberOfCases' ? parseInt(value) || '' : parseFloat(value) || '')
                        }
                    }
                }));
            }
        }
    };

    // Calculate total TAT percentage
    const calculateTotalTatPercentage = () => {
        return Object.values(formData.tatCategories).reduce((total, category) => {
            const tatValue = parseFloat(category.tatValue) || 0;
            const numberOfCases = parseInt(category.numberOfCases) || 0;
            return numberOfCases > 0 ? total + tatValue : total;
        }, 0);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate that at least one category has data
        const hasData = Object.values(formData.tatCategories).some(category =>
            category.numberOfCases && category.tatValue
        );

        if (!hasData) {
            toast.error('Please enter data for at least one TAT category');
            return;
        }

        // Calculate total TAT percentage for validation
        const totalTatPercentage = calculateTotalTatPercentage();

        if (Math.abs(totalTatPercentage - 100) > 0.01) { // Allow small floating point differences
            toast.error(`Total TAT percentage must equal 100%. Current total: ${totalTatPercentage.toFixed(2)}%`);
            return;
        }

        if (isEditing) {
            // For editing, send the entire form data
            const updateData = {
                process: formData.process,
                tatCategories: formData.tatCategories
            };
            dispatch(updateTatAnalysis(editId, updateData));
        } else {
            // For new submissions, convert form data to multiple submissions
            const submissions = [];

            Object.keys(formData.tatCategories).forEach(category => {
                const categoryData = formData.tatCategories[category];
                if (categoryData.numberOfCases && categoryData.tatValue) {
                    submissions.push({
                        process: formData.process,
                        tatCategory: category,
                        numberOfCases: categoryData.numberOfCases,
                        tatValue: categoryData.tatValue
                    });
                }
            });

            // Submit each category separately
            submissions.forEach(submission => {
                dispatch(submitTatAnalysis(submission));
            });
        }
    };

    const handleCloseModal = () => {
        setIsEditing(false);
        setEditId(null);

        const selectedProcess = getVerificationProcessesData?.data?.length > 0 ? getVerificationProcessesData.data[0]._id : '';
        setFormData({
            process: selectedProcess,
            tatCategories: {
                '0-2': { numberOfCases: '', tatValue: '' },
                '2-4': { numberOfCases: '', tatValue: '' },
                '4-6': { numberOfCases: '', tatValue: '' },
                '6-8': { numberOfCases: '', tatValue: '' },
                '>8': { numberOfCases: '', tatValue: '' }
            }
        });

        setIsOpen(false);

        // Reset states
        dispatch({ type: SUBMIT_TAT_ANALYSIS_RESET });
        dispatch({ type: UPDATE_TAT_ANALYSIS_RESET });
    };

    const handleUpdate = (group) => {
        // Reset all categories first
        const resetCategories = {
            '0-2': { numberOfCases: '', tatValue: '' },
            '2-4': { numberOfCases: '', tatValue: '' },
            '4-6': { numberOfCases: '', tatValue: '' },
            '6-8': { numberOfCases: '', tatValue: '' },
            '>8': { numberOfCases: '', tatValue: '' }
        };

        // Fill in the data from the group
        Object.keys(group.categories).forEach(category => {
            const categoryData = group.categories[category];
            if (categoryData) {
                resetCategories[category] = {
                    numberOfCases: categoryData.numberOfCases.toString(),
                    tatValue: categoryData.tatValue.toString()
                };
            }
        });

        setFormData({
            process: group.processId,
            tatCategories: resetCategories
        });

        setIsEditing(true);
        setEditId(group.allIds[0]); // Use the first ID as reference
        setEditGroup(group);
        setIsOpen(true);
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this TAT analysis?')) {
            dispatch(deleteTatAnalysis(id));
        }
    };

    const handleDeleteGroup = (group, showConfirmation = true) => {
        const confirmDelete = showConfirmation ?
            window.confirm('Are you sure you want to delete this entry?') :
            true;

        if (confirmDelete) {
            // Use the allIds array to delete all entries in the group
            group.allIds.forEach(id => {
                dispatch(deleteTatAnalysis(id));
            });
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleItemsPerPageChange = (newLimit) => {
        setLimit(newLimit);
        setPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPage(1);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const stats = tatStatisticsData?.stats || {
        totalCases: 0,
        avgTatValue: 0,
        totalEntries: 0,
        categoryStats: {
            '0-2': { totalCases: 0, avgTatValue: 0, count: 0 },
            '2-4': { totalCases: 0, avgTatValue: 0, count: 0 },
            '4-6': { totalCases: 0, avgTatValue: 0, count: 0 },
            '6-8': { totalCases: 0, avgTatValue: 0, count: 0 },
            '>8': { totalCases: 0, avgTatValue: 0, count: 0 }
        }
    };

    const tatAnalysisList = myTatAnalysisData?.data || [];
    const totalPages = myTatAnalysisData?.totalPages || 1;
    const totalItems = myTatAnalysisData?.total || 0;
    const assignedProcesses = getVerificationProcessesData?.data || [];

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto p-6 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">TAT Analysis</h1>
                        <p className="text-gray-600 mt-1">Track and analyze turnaround time performance</p>
                    </div>
                    <button
                        onClick={() => {
                            setIsEditing(false);
                            setEditId(null);
                            setEditGroup(null);
                            const selectedProcess = assignedProcesses.length > 0 ? assignedProcesses[0]._id : '';
                            setFormData({
                                process: selectedProcess,
                                tatCategories: {
                                    '0-2': { numberOfCases: '', tatValue: '' },
                                    '2-4': { numberOfCases: '', tatValue: '' },
                                    '4-6': { numberOfCases: '', tatValue: '' },
                                    '6-8': { numberOfCases: '', tatValue: '' },
                                    '>8': { numberOfCases: '', tatValue: '' }
                                }
                            });
                            setIsOpen(true);
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                    >
                        <Plus size={18} />
                        Add Analysis
                    </button>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="text"
                                    placeholder="Search entries..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400"
                                />
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <input
                                type="date"
                                value={dateFilter.startDate}
                                onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400"
                            />
                            <input
                                type="date"
                                value={dateFilter.endDate}
                                onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400"
                            />
                            <button
                                onClick={() => {
                                    setSearchTerm('');
                                    setDateFilter({ startDate: '', endDate: '' });
                                    setPage(1);
                                }}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {/* Data Table */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-lg font-medium text-gray-900">Analysis Data</h2>
                    </div>

                    {loadingMyTatAnalysis ? (
                        <div className="p-6 text-center text-gray-500">Loading data...</div>
                    ) : tatAnalysisList.length > 0 ? (
                        <>
                            {/* Group data by process and date */}
                            {(() => {
                                const groupedData = tatAnalysisList.reduce((acc, item) => {
                                    // Add null check for item.process
                                    if (!item.process || !item.process._id) {
                                        console.warn('Skipping item with missing process:', item);
                                        return acc;
                                    }

                                    const dateStr = formatDate(item.date);
                                    const key = `${item.process._id}-${dateStr}`;

                                    if (!acc[key]) {
                                        acc[key] = {
                                            process: item.process.process,
                                            processId: item.process._id,
                                            date: item.date,
                                            dateStr: dateStr,
                                            categories: {},
                                            totalCases: 0,
                                            weightedTatSum: 0,
                                            allIds: []
                                        };
                                    }

                                    const existingCategory = acc[key].categories[item.tatCategory];
                                    if (!existingCategory || new Date(item.updatedAt) > new Date(existingCategory.updatedAt)) {
                                        acc[key].categories[item.tatCategory] = {
                                            numberOfCases: item.numberOfCases,
                                            tatValue: item.tatValue,
                                            id: item._id,
                                            updatedAt: item.updatedAt
                                        };
                                    }

                                    if (!acc[key].allIds.includes(item._id)) {
                                        acc[key].allIds.push(item._id);
                                    }

                                    return acc;
                                }, {});


                                const groupedArray = Object.values(groupedData).map(group => {
                                    let totalCases = 0;
                                    let weightedTatSum = 0;

                                    Object.values(group.categories).forEach(cat => {
                                        totalCases += cat.numberOfCases;
                                        weightedTatSum += (cat.numberOfCases * cat.tatValue);
                                    });

                                    return {
                                        ...group,
                                        totalCases,
                                        weightedTatSum,
                                        totalTatPercentage: totalCases > 0 ? (weightedTatSum / totalCases).toFixed(1) : '0.0'
                                    };
                                });

                                return (
                                    <div>
                                        {/* Desktop Table */}
                                        <div className="hidden lg:block overflow-x-auto">
                                            <table className="w-full">
                                                <thead className="bg-gray-50 border-b border-gray-200">
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verification Process</th>
                                                        {tatCategories.map((category) => (
                                                            <th key={category} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                                                                <div>{category}h</div>
                                                                <div className="text-gray-400 font-normal mt-1">Cases | %</div>
                                                            </th>
                                                        ))}
                                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">Total Cases</th>
                                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {(() => {
                                                        // Calculate totals
                                                        const totals = {
                                                            totalCasesSum: 0,
                                                            categoryTotals: {}
                                                        };


                                                        tatCategories.forEach(category => {
                                                            totals.categoryTotals[category] = {
                                                                totalCases: 0,
                                                                weightedTatSum: 0, // Sum of (cases × TAT%)
                                                                avgTatPercentage: 0,
                                                                entriesCount: 0
                                                            };
                                                        });


                                                        // Calculate totals from grouped data
                                                        groupedArray.forEach(group => {
                                                            let groupTotalCases = 0;

                                                            tatCategories.forEach(category => {
                                                                const categoryData = group.categories[category];
                                                                if (categoryData) {
                                                                    const cases = categoryData.numberOfCases;
                                                                    const tatValue = categoryData.tatValue;
                                                                    const weightedTat = cases * tatValue;

                                                                    totals.categoryTotals[category].totalCases += cases;
                                                                    totals.categoryTotals[category].weightedTatSum += weightedTat;
                                                                    totals.categoryTotals[category].entriesCount += 1;
                                                                    groupTotalCases += cases;
                                                                }
                                                            });

                                                            totals.totalCasesSum += groupTotalCases;
                                                        });

                                                        // Calculate weighted averages for each category
                                                        tatCategories.forEach(category => {
                                                            const categoryData = totals.categoryTotals[category];
                                                            if (categoryData.totalCases > 0) {
                                                                categoryData.avgTatPercentage =
                                                                    parseFloat((categoryData.weightedTatSum / categoryData.totalCases).toFixed(1));
                                                            }
                                                        });


                                                        return (
                                                            <>
                                                                {/* Data Rows */}
                                                                {groupedArray.map((group, index) => {
                                                                    const groupTotalCases = Object.values(group.categories).reduce((sum, cat) => sum + (cat?.numberOfCases || 0), 0);

                                                                    return (
                                                                        <tr key={index} className="hover:bg-gray-50">
                                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-100">
                                                                                {group.dateStr}
                                                                            </td>
                                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-100">
                                                                                <div className="max-w-32 truncate" title={group.process}>
                                                                                    {group.process}
                                                                                </div>
                                                                            </td>
                                                                            {tatCategories.map((category) => {
                                                                                const categoryData = group.categories[category];
                                                                                return (
                                                                                    <td key={category} className="px-4 py-4 text-center text-sm border-l border-gray-200">
                                                                                        {categoryData ? (
                                                                                            <div className="font-medium text-gray-900">
                                                                                                {categoryData.numberOfCases.toLocaleString()} | {categoryData.tatValue.toFixed(1)}%
                                                                                            </div>
                                                                                        ) : (
                                                                                            <div className="text-gray-400">0 | 0.0%</div>
                                                                                        )}
                                                                                    </td>
                                                                                );
                                                                            })}
                                                                            <td className="px-4 py-4 text-center text-sm font-medium text-gray-900 border-l border-gray-100 bg-yellow-50">
                                                                                {groupTotalCases.toLocaleString()}
                                                                            </td>
                                                                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm border-l border-gray-100">
                                                                                <div className="flex justify-center gap-2">
                                                                                    <button
                                                                                        onClick={() => handleUpdate(group)}
                                                                                        className="inline-flex items-center gap-1 px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
                                                                                        title="Edit"
                                                                                    >
                                                                                        <Edit size={12} />
                                                                                        Edit
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => handleDeleteGroup(group)}
                                                                                        className="inline-flex items-center gap-1 px-3 py-1 text-xs text-red-600 border border-red-300 rounded hover:bg-red-50"
                                                                                        title="Delete"
                                                                                    >
                                                                                        <Trash2 size={12} />
                                                                                        Delete
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}

                                                                {/* Totals Row */}
                                                                {groupedArray.length > 0 && (
                                                                    <tr className="bg-gray-100 border-t-2 border-gray-300 font-medium">
                                                                        <td colSpan="2" className="px-6 py-4 text-sm font-semibold text-gray-900">
                                                                            TOTALS
                                                                        </td>
                                                                        {tatCategories.map((category) => (
                                                                            <td key={category} className="px-4 py-4 text-center text-sm font-semibold text-gray-900 border-l border-gray-200">
                                                                                {totals.categoryTotals[category].totalCases.toLocaleString()} | {totals.categoryTotals[category].avgTatPercentage.toFixed(1)}%
                                                                            </td>
                                                                        ))}
                                                                        <td className="px-6 py-4 text-center text-sm font-semibold text-gray-900 border-l border-gray-200">
                                                                            {totals.totalCasesSum.toLocaleString()}
                                                                        </td>
                                                                        <td className="px-6 py-4 border-l border-gray-200">
                                                                            {/* Empty actions cell for totals row */}
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Mobile Cards */}
                                        <div className="lg:hidden">
                                            <div className="p-4 space-y-4">
                                                {groupedArray.map((group, index) => (
                                                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                                                        <div className="flex justify-between items-start mb-3">
                                                            <div>
                                                                <div className="font-medium text-gray-900">{group.process}</div>
                                                                <div className="text-sm text-gray-600">{group.dateStr}</div>
                                                            </div>
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={() => handleUpdate(group)}
                                                                    className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                                                                >
                                                                    <Edit size={16} />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteGroup(group)}
                                                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <div className="grid grid-cols-2 gap-3">
                                                            {tatCategories.map((category) => {
                                                                const categoryData = group.categories[category];
                                                                return (
                                                                    <div key={category} className="p-3 bg-gray-50 rounded border">
                                                                        <div className="text-xs text-gray-600 mb-1">{category} Hours</div>
                                                                        {categoryData ? (
                                                                            <div>
                                                                                <div className="text-sm font-medium text-gray-900">
                                                                                    {categoryData.numberOfCases.toLocaleString()} cases
                                                                                </div>
                                                                                <div className="text-xs text-gray-600">
                                                                                    {categoryData.tatValue.toFixed(1)}% TAT
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-sm text-gray-400">No data</div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Pagination */}
                            <EnhancedPagination
                                currentPage={page}
                                totalPages={totalPages}
                                totalItems={totalItems}
                                itemsPerPage={limit}
                                onPageChange={handlePageChange}
                                onItemsPerPageChange={handleItemsPerPageChange}
                                maxVisiblePages={5}
                                showItemsPerPage={true}
                                showInfo={true}
                            />
                        </>
                    ) : (
                        <div className="p-12 text-center text-gray-500">
                            <BarChart3 size={48} className="mx-auto mb-4 text-gray-300" />
                            <div className="text-lg font-medium mb-2">No data found</div>
                            <div className="text-sm">
                                {searchTerm ? 'Try adjusting your search criteria' : 'Start by adding your first TAT analysis'}
                            </div>
                        </div>
                    )}
                </div>

                {/* Modal */}
                {isOpen && (
                    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-200">
                            <div className="flex items-center justify-between p-6 border-b border-gray-200">
                                <h2 className="text-lg font-medium text-gray-900">
                                    {isEditing ? 'Edit Analysis' : 'Add New Analysis'}
                                </h2>
                                <button
                                    onClick={handleCloseModal}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="p-6 space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Verification Process</label>
                                    <select
                                        name="process"
                                        value={formData.process}
                                        onChange={handleChange}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-400"
                                        disabled={isEditing}
                                        required
                                    >
                                        <option value="">Select Verification Process</option>
                                        {assignedProcesses.map((process) => (
                                            <option key={process._id} value={process._id}>
                                                {process.process}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-4">
                                        TAT Categories
                                    </label>

                                    {/* TAT Total Display */}
                                    <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm font-medium text-gray-700">Total TAT Percentage:</span>
                                            <span className={`text-lg font-semibold ${Math.abs(calculateTotalTatPercentage() - 100) < 0.01 && calculateTotalTatPercentage() > 0
                                                ? 'text-green-600'
                                                : 'text-red-600'
                                                }`}>
                                                {calculateTotalTatPercentage().toFixed(2)}%
                                            </span>
                                        </div>
                                        {calculateTotalTatPercentage() > 0 && Math.abs(calculateTotalTatPercentage() - 100) > 0.01 && (
                                            <div className="text-xs text-red-600 mt-1">
                                                Total must equal 100%
                                            </div>
                                        )}
                                    </div>

                                    {/* Desktop Layout */}
                                    <div className="hidden md:block">
                                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                                            <table className="w-full">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Cases</th>
                                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">TAT %</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {tatCategories.map((category) => (
                                                        <tr key={category}>
                                                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                                {category} Hours
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <input
                                                                    type="number"
                                                                    name={`${category}-numberOfCases`}
                                                                    value={formData.tatCategories[category].numberOfCases}
                                                                    onChange={handleChange}
                                                                    min="0"
                                                                    placeholder="0"
                                                                    className="w-full text-center border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-gray-400"
                                                                />
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <input
                                                                    type="number"
                                                                    name={`${category}-tatValue`}
                                                                    value={formData.tatCategories[category].tatValue}
                                                                    onChange={handleChange}
                                                                    min="0"
                                                                    max="100"
                                                                    step="0.01"
                                                                    placeholder="0.00"
                                                                    className="w-full text-center border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-gray-400"
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Mobile Layout */}
                                    <div className="md:hidden space-y-4">
                                        {tatCategories.map((category) => (
                                            <div key={category} className="p-4 border border-gray-200 rounded-lg">
                                                <div className="text-sm font-medium text-gray-900 mb-3">{category} Hours</div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-xs text-gray-600 mb-1">Cases</label>
                                                        <input
                                                            type="number"
                                                            name={`${category}-numberOfCases`}
                                                            value={formData.tatCategories[category].numberOfCases}
                                                            onChange={handleChange}
                                                            min="0"
                                                            placeholder="0"
                                                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs text-gray-600 mb-1">TAT %</label>
                                                        <input
                                                            type="number"
                                                            name={`${category}-tatValue`}
                                                            value={formData.tatCategories[category].tatValue}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="100"
                                                            step="0.01"
                                                            placeholder="0.00"
                                                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loadingSubmitTatAnalysis || loadingUpdateTatAnalysis}
                                        className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {(loadingSubmitTatAnalysis || loadingUpdateTatAnalysis)
                                            ? "Processing..."
                                            : isEditing ? "Update Analysis" : "Submit Analysis"
                                        }
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TatAnalysis;